/**
 * THE TMS TECHNICAL-OFFICIALS DAY PUSH - everything that is the same whatever carries the request.
 *
 * Authority: the supervised capture of TMS's technical write flow (18 September 2026); the
 * read-only survey of the real day page (the 20 September men's day, competition 1916);
 * `12-tms-push-extension.md` (the contract: sections 4-6a - the extension runs this, the API
 * accepts its results); `09-tms-write-paths.md` section 2 (read-modify-write, day-and-competition
 * batching, the Official-match backstop) and sections 5 and 5.1 (draft-only, never retried, fail
 * closed); and `apps/api/src/tms/push-gateway.ts`, whose outcome and failure words this module
 * speaks unchanged (it cannot import them: shared never depends on the API).
 *
 * WHAT TMS DOES, AS OBSERVED
 *
 *  * Appointments are day-based. `GET {base}/admin/content/competitions/{comp}/appointments/{date}`
 *    shows the day; its Technical tab holds ONE form posting to `.../competitions/{comp}/technical`.
 *  * The form carries `_token` (Laravel's CSRF token) and `date`, and per match a table row with
 *    three selects `m_{id}_SJ`, `m_{id}_TJ`, `m_{id}_TO` (option values are TMS person ids; "0"
 *    is none, and the held choice is marked `selected`) and a publish checkbox
 *    `m_{id}_pub_technical`.
 *  * ONE post saves the WHOLE day for that competition. Saved appointments are private until
 *    published, and publishing is the TD's own act inside TMS.
 *  * An Official match still shows its crew, but its three selects AND its publish box are
 *    `disabled`, and its Time cell ends "Official". TMS's words: "Appointments cannot be changed
 *    for Official Matches".
 *  * Publishing and unpublishing a day are plain GET links (`.../{date}/publish/...` and
 *    `.../{date}/unpublish/...`), so a careless link-follow, redirect-follow or prefetch could
 *    publish or unpublish a whole day. Hence {@link guardTmsTechnicalRequest}.
 *  * Laravel answers a save - accepted OR refused - with a redirect back to the day page, and
 *    flashes its message into the session for exactly ONE next page load. A refused save flashes
 *    the submitted input as well, and the form re-renders that input INSTEAD of what TMS holds.
 *
 * THE DESIGN IN ONE SENTENCE: REPLICATE THE BROWSER
 *
 * The body we post is the exact list a browser would submit for the form as TMS rendered it
 * (`formEntryList`), in the same order, with ONLY our planned `m_{id}_{TO|SJ|TJ}` values put in.
 * Everything else - hidden fields nobody told us about, other matches, every publish box, every
 * disabled Official row (which a browser leaves out entirely) - goes back exactly as the TD's
 * own Save button would send it. A checkbox missing from a form post reads as UNTICKED to the
 * server, so a core that assembled its own body could unpublish a match simply by forgetting it;
 * echoing the browser's list cannot. Where the page does not say for certain what the browser
 * would send - a slot with no option marked `selected`, a field PHP would fold into one of ours -
 * the push is refused rather than guessed.
 *
 * THE FLOW ({@link pushTechnicalDay})
 *
 *   read the day (and read it again if that load showed error alerts: it may be an earlier
 *   refused save's flash, showing old input) -> parse -> plan (a refusal sends nothing) -> the
 *   caller's last word (`beforePost`, the extension's hold check) -> POST exactly once, never
 *   retried -> read the day for TMS's WORDS (this load takes any flash) -> read it AGAIN for the
 *   SLOTS -> verify every slot.
 *
 * Why two reads after the post: after a refused save the NEXT page load shows what we SENT, not
 * what TMS holds. With `redirect: "manual"`, or a lost answer, that next load is ours - so slots
 * are only ever believed from a LATER load, and a new error alert on any page after the post means
 * TMS refused, whatever the slots say. The read-back is the receipt; the toast is only words.
 *
 * WHERE IT RUNS
 *
 * Unchanged in the API server, in a browser page and in a Chrome MV3 extension service worker:
 * no DOM, no platform API, no network, no clock except the one passed in. The one thing that
 * differs between authentication designs is the {@link TmsTechnicalTransport}.
 *
 * NOTHING HERE MAY BE LOGGED WHOLE
 *
 * A snapshot and a planned body carry the form's `_token`, a live CSRF token. Every string this
 * module returns is passed through a redactor that removes the tokens it saw and the body it
 * sent, strips control characters and is clipped to the API's bounds.
 */
import { asciiLower, collapseWhitespace, encodeFormUrlEncoded, formEntryList, readHtmlPage, } from "./tms-html-form.js";
import { TMS_APPOINTMENT_PUSH_SLOTS, appointmentCompetitionWord, summariseAppointmentPayload, } from "./tms-appointment-push.js";
export { MAX_HTML_DEPTH, MAX_HTML_PAGE_CHARS, decodeCharacterReferences, encodeFormUrlEncoded, formEntryList, readHtmlPage, } from "./tms-html-form.js";
/* -- Constants ------------------------------------------------------------------------------ */
/**
 * The production TMS origin - the ONLY origin requests may go to, unless a test says otherwise
 * explicitly (see {@link TmsTechnicalOriginOptions.testOnlyOrigin}). Data only: nothing in this
 * module opens a connection to anything.
 */
export const TMS_ORIGIN = "https://tms.fih.ch";
/** The only competitions this build may write to (09 section 1): men 1916, women 1917. */
export const TMS_TECHNICAL_COMPETITION_IDS = ["1916", "1917"];
/** TMS's own words for its Official-match lock, quoted wherever we decline to touch one. */
export const TMS_OFFICIAL_MATCH_RULE = "Appointments cannot be changed for Official Matches";
/** The start of TMS's success toast: "Technical Officials saved for date ...". */
export const TMS_TECHNICAL_SAVED_TEXT = "Technical Officials saved";
/** Why a planned match whose publish box is ticked is not pushed (the default policy). */
export const TMS_PUBLISHED_MATCH_RULE = "Already published in TMS - unpublish it there first, or enter the change by hand.";
/**
 * The bounds on what this module returns. `detail`, `collateral` and `collateralCount` and
 * `payloads` are the API's own (12 section 6a); `fragment` bounds any one piece of text taken
 * from a TMS page or a transport's error.
 */
export const TMS_TECHNICAL_LIMITS = {
    detail: 2000,
    collateral: 300,
    collateralCount: 100,
    payloads: 64,
    fragment: 160,
};
/* -- Text that leaves this module ----------------------------------------------------------- */
/**
 * Control characters (C0, DEL, C1), zero-width and bidirectional formatting characters and line
 * separators: never passed on, so no page or error can reorder or hide what a TD reads. Built
 * from numbers so that this file stays pure ASCII.
 */
const UNSAFE_TEXT = new RegExp(`[\\x00-\\x1f\\x7f-\\x9f${[
    [0x200b, 0x200f],
    [0x2028, 0x202e],
    [0x2060, 0x2069],
    [0xfeff, 0xfeff],
]
    .map(([from, to]) => `${String.fromCharCode(from ?? 0)}-${String.fromCharCode(to ?? 0)}`)
    .join("")}]`, "g");
function clipText(text, limit) {
    if (text.length <= limit)
        return text;
    let cut = Math.max(0, limit - 3);
    const last = text.charCodeAt(cut - 1);
    if (last >= 0xd800 && last <= 0xdbff)
        cut -= 1; // never split a surrogate pair
    return `${text.slice(0, cut)}...`;
}
/** Control characters out, whitespace collapsed, clipped to `limit`. */
export function cleanTmsText(text, limit = TMS_TECHNICAL_LIMITS.fragment) {
    return clipText(collapseWhitespace(String(text).replace(UNSAFE_TEXT, " ")), limit);
}
/** One piece of text from a page or an error, fit to quote inside a sentence. */
function fragment(text) {
    return cleanTmsText(text, TMS_TECHNICAL_LIMITS.fragment);
}
const REDACTED = "[redacted]";
/**
 * Removes the secrets a push handled - every form token it saw and the body it sent - from any
 * string before it leaves. A whole token, its URL-encoded form, any `_token=` value, and any run
 * of ten or more characters taken from anywhere in a token (a transport's error may quote any
 * slice of the body) are all replaced.
 */
export class Redactor {
    secrets = [];
    tokens = [];
    addToken(token) {
        if (token.length === 0 || this.tokens.includes(token))
            return;
        this.tokens.push(token);
        this.addSecret(token);
        this.addSecret(encodeURIComponent(token));
    }
    addSecret(secret) {
        if (secret.length < 8 || this.secrets.includes(secret))
            return;
        this.secrets.push(secret);
        this.secrets.sort((a, b) => b.length - a.length);
    }
    apply(text) {
        let out = text;
        for (const secret of this.secrets)
            if (out.includes(secret))
                out = out.split(secret).join(REDACTED);
        out = out.replace(/_token=[^&\s"'<>]*/g, `_token=${REDACTED}`);
        for (const token of this.tokens) {
            // Longest slices first, so a long quoted slice goes whole rather than piece by piece.
            const longest = Math.min(token.length - 1, 128);
            for (let length = longest; length >= 10; length -= 1) {
                for (let start = 0; start + length <= token.length && start <= 128; start += 1) {
                    const slice = token.slice(start, start + length);
                    if (out.includes(slice))
                        out = out.split(slice).join(REDACTED);
                }
            }
        }
        return out;
    }
    /** Untrusted text (a transport error, a page's words): capped, redacted, cleaned, clipped. */
    fragment(text) {
        return fragment(this.apply(String(text).slice(0, 10_000)));
    }
    /** A string about to leave the module: redacted, cleaned, clipped to `limit`. */
    final(text, limit) {
        return cleanTmsText(this.apply(String(text)), limit);
    }
}
function errorMessage(error) {
    try {
        const text = error instanceof Error ? error.message : String(error);
        return text === "" ? "no reason given" : text;
    }
    catch {
        return "an error that could not be described";
    }
}
function quoted(texts) {
    const shown = texts.slice(0, 3).map((text) => `"${text}"`);
    const more = texts.length > 3 ? ` and ${String(texts.length - 3)} more` : "";
    return `${shown.join(" and ")}${more}`;
}
/* -- Validation of our own arguments (programmer errors, thrown before any request) --------- */
const CALENDAR_DATE = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/;
const HTTPS_ORIGIN = /^https:\/\/([A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*)(?::([0-9]{1,5}))?\/?$/;
const RESERVED_TEST_HOST = /^(?:localhost|.+\.(?:invalid|test|example|localhost))$/;
const PERSON_ID_SHAPE = /^[0-9]{1,12}$/;
/** `YYYY-MM-DD` naming a real calendar date (no 30 February, no month 13). */
export function isTmsCalendarDate(value) {
    const match = typeof value === "string" ? CALENDAR_DATE.exec(value) : null;
    if (match === null)
        return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (year < 1 || month < 1 || month > 12 || day < 1)
        return false;
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    const lengths = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day <= (lengths[month - 1] ?? 0);
}
function requireCalendarDate(date, where) {
    if (!isTmsCalendarDate(date)) {
        throw new Error(`${where}: "${fragment(String(date))}" is not a calendar date written YYYY-MM-DD.`);
    }
}
function requireCompetitionDigits(competitionId, where) {
    if (typeof competitionId !== "string" || !/^[0-9]{1,9}$/.test(competitionId)) {
        throw new Error(`${where}: the competition id "${fragment(String(competitionId))}" must be digits only.`);
    }
}
/** The origin without a trailing slash, or a thrown error: only a bare https origin will do. */
function requireHttpsOrigin(base, where) {
    const match = typeof base === "string" && base.length <= 300 ? HTTPS_ORIGIN.exec(base) : null;
    const port = match?.[2] === undefined ? null : Number(match[2]);
    if (match === null || (port !== null && (port < 1 || port > 65535))) {
        throw new Error(`${where}: the TMS base "${fragment(String(base))}" must be a bare https origin (scheme and host, ` +
            "no path, query, fragment, user or password).");
    }
    return base.endsWith("/") ? base.slice(0, -1) : base;
}
/** An https origin on a host reserved for tests by RFC 2606 and 6761 - never a real server. */
export function isReservedTestOrigin(base) {
    const match = typeof base === "string" && base.length <= 300 ? HTTPS_ORIGIN.exec(base) : null;
    return match !== null && RESERVED_TEST_HOST.test(asciiLower(match[1] ?? ""));
}
/** The pinned origin: {@link TMS_ORIGIN}, or - only when a test says so - a reserved test host. */
export function requirePinnedOrigin(base, options, where) {
    const origin = requireHttpsOrigin(base, where);
    if (asciiLower(origin) === asciiLower(TMS_ORIGIN))
        return origin;
    if (options.testOnlyOrigin === true && isReservedTestOrigin(origin))
        return origin;
    throw new Error(`${where}: requests go only to ${TMS_ORIGIN}. "${fragment(origin)}" is allowed only as an explicit ` +
        "test-only origin on a reserved test host (.invalid, .test, .example, localhost).");
}
function hasLetterWord(text) {
    return nameWords(text).some((word) => /\p{L}/u.test(word));
}
/**
 * Check every payload's WHOLE shape before any request: a malformed payload found after the post
 * would leave a push that may have landed with no result to say so. Throws, naming the problem.
 */
export function validateTechnicalPayloads(payloads, competitionId, date, where = "validateTechnicalPayloads") {
    const fail = (why) => {
        throw new Error(`${where}: ${why}`);
    };
    if (!Array.isArray(payloads))
        fail("the payloads are not a list.");
    const list = payloads;
    if (list.length === 0)
        fail("there is nothing to push.");
    if (list.length > TMS_TECHNICAL_LIMITS.payloads) {
        fail(`one push carries at most ${String(TMS_TECHNICAL_LIMITS.payloads)} matches, not ${String(list.length)}.`);
    }
    const seen = new Set();
    list.forEach((raw, index) => {
        const at = `payload ${String(index + 1)}`;
        if (raw === null || typeof raw !== "object")
            fail(`${at} is not a payload.`);
        const payload = raw;
        if (payload["kind"] !== "appointments")
            fail(`${at} is not an appointments payload.`);
        const id = payload["tmsMatchDbId"];
        if (typeof id !== "number" || !Number.isSafeInteger(id) || id <= 0)
            fail(`${at} has no whole-number TMS match id.`);
        const match = `match ${String(id)}`;
        if (payload["competitionId"] !== competitionId || payload["date"] !== date) {
            fail(`the payload for ${match} is for competition ${fragment(String(payload["competitionId"]))} on ` +
                `${fragment(String(payload["date"]))}, not competition ${competitionId} on ${date}. One push is one competition-day.`);
        }
        if (payload["competitionGender"] !== "m" && payload["competitionGender"] !== "w")
            fail(`the payload for ${match} has no competition gender.`);
        if (typeof payload["displayMatchNo"] !== "string")
            fail(`the payload for ${match} has no match number.`);
        for (const team of ["homeTeamCode", "awayTeamCode"]) {
            const code = payload[team];
            if (code !== null && typeof code !== "string")
                fail(`the payload for ${match} has a ${team} that is not text.`);
        }
        const slots = payload["slots"];
        if (slots === null || typeof slots !== "object")
            fail(`the payload for ${match} has no crew.`);
        for (const slot of TMS_APPOINTMENT_PUSH_SLOTS) {
            const official = slots[slot];
            if (official === null || typeof official !== "object")
                fail(`the ${slot} of ${match} is missing.`);
            const person = official;
            const personId = person["tmsPersonId"];
            if (typeof personId !== "string" || !PERSON_ID_SHAPE.test(personId))
                fail(`the ${slot} of ${match} has no TMS person id made of digits.`);
            const name = person["mediaName"];
            if (typeof name !== "string" || name.length > 200 || !hasLetterWord(name)) {
                fail(`the ${slot} of ${match} has no name to check against TMS's own.`);
            }
        }
        if (seen.has(id))
            fail(`${match} appears twice in one push.`);
        seen.add(id);
    });
}
/**
 * What is wrong with a transport's answer, or null. A missing or non-integer status must never
 * be read as a success. It reads the answer's properties directly: a transport whose answer
 * throws when read is an unexpected failure, handled as one.
 */
export function tmsHttpResponseProblem(response) {
    if (response === null || typeof response !== "object")
        return "the transport answered with something that is not a response";
    const answer = response;
    const status = answer["status"];
    if (typeof status !== "number" || !Number.isInteger(status) || !(status === 0 || (status >= 100 && status <= 599))) {
        return `the transport answered without a valid HTTP status (${fragment(String(status))})`;
    }
    if (typeof answer["url"] !== "string")
        return "the transport answered without the address the answer came from";
    if (typeof answer["redirected"] !== "boolean")
        return "the transport did not say whether it followed a redirect";
    if (typeof answer["body"] !== "string")
        return "the transport answered without a body";
    return null;
}
/* -- 1. The two URLs ------------------------------------------------------------------------ */
/** `{base}/admin/content/competitions/{comp}/appointments/{YYYY-MM-DD}` - the day page. */
export function tmsTechnicalDayPageUrl(base, competitionId, date) {
    const origin = requireHttpsOrigin(base, "tmsTechnicalDayPageUrl");
    requireCompetitionDigits(competitionId, "tmsTechnicalDayPageUrl");
    requireCalendarDate(date, "tmsTechnicalDayPageUrl");
    return `${origin}/admin/content/competitions/${competitionId}/appointments/${date}`;
}
/** `{base}/admin/content/competitions/{comp}/technical` - where the technical form posts. */
export function tmsTechnicalPostUrl(base, competitionId) {
    const origin = requireHttpsOrigin(base, "tmsTechnicalPostUrl");
    requireCompetitionDigits(competitionId, "tmsTechnicalPostUrl");
    return `${origin}/admin/content/competitions/${competitionId}/technical`;
}
/* -- The URL guard: exactly two requests, only to TMS, and never a publish link ------------- */
/** Thrown by {@link guardTmsTechnicalRequest}: a request this build must never make. */
export class TmsRequestRefusedError extends Error {
    constructor(message) {
        super(message);
        this.name = "TmsRequestRefusedError";
    }
}
const DAY_PAGE_PATH = /^\/admin\/content\/competitions\/([0-9]+)\/appointments\/([0-9]{4}-[0-9]{2}-[0-9]{2})$/;
const TECHNICAL_POST_PATH = /^\/admin\/content\/competitions\/([0-9]+)\/technical$/;
function writableCompetition(value) {
    return TMS_TECHNICAL_COMPETITION_IDS.find((id) => id === value) ?? null;
}
/**
 * The allow-list every request passes before it is made - by the orchestration here, and by
 * every transport again. It allows EXACTLY two shapes and throws for anything else:
 *
 *   GET  {base}/admin/content/competitions/{1916|1917}/appointments/{YYYY-MM-DD}
 *   POST {base}/admin/content/competitions/{1916|1917}/technical
 *
 * `base` must be {@link TMS_ORIGIN} unless `options.testOnlyOrigin` allows a reserved test host.
 * Exact means exact: that origin, no query, no fragment, no trailing slash, no extra segment, a
 * real calendar date, the right method for the shape, at most 2048 characters. And any address
 * that mentions "publish" in any spelling (`/publish/...`, `/unpublish/...`, percent-encoded) is
 * refused by name first: in TMS those are plain GET links that publish or unpublish a whole day.
 */
export function guardTmsTechnicalRequest(method, url, base, options = {}) {
    const origin = requirePinnedOrigin(base, options, "guardTmsTechnicalRequest");
    const refuse = (why) => {
        throw new TmsRequestRefusedError(`Refused ${fragment(String(method))} ${fragment(String(url))}: ${why}. The only requests this build makes are ` +
            `GET ${origin}/admin/content/competitions/{1916|1917}/appointments/{YYYY-MM-DD} and ` +
            `POST ${origin}/admin/content/competitions/{1916|1917}/technical.`);
    };
    if (typeof url !== "string" || typeof method !== "string")
        return refuse("it is not a method and an address");
    if (url.length > 2048)
        return refuse("its address is too long");
    let decoded;
    try {
        decoded = decodeURIComponent(url);
    }
    catch {
        return refuse("its address is not well-formed");
    }
    if (asciiLower(decoded).includes("publish")) {
        return refuse("it is a TMS publish or unpublish link. Publishing is a manual act by the TD inside TMS, " +
            "and this build never requests one");
    }
    const match = /^https:\/\/([^/?#]*)(\/[^?#]*)$/.exec(url);
    if (match === null)
        return refuse("it is not an https address without a query or a fragment");
    if (asciiLower(`https://${match[1] ?? ""}`) !== asciiLower(origin)) {
        return refuse(`it is not on the pinned TMS origin ${origin}`);
    }
    const path = match[2] ?? "";
    const day = DAY_PAGE_PATH.exec(path);
    if (day !== null) {
        if (method !== "GET")
            return refuse("a day page is only ever read, with GET");
        const competitionId = writableCompetition(day[1]) ?? refuse(`competition ${day[1] ?? ""} is not 1916 or 1917`);
        const date = day[2] ?? "";
        if (!isTmsCalendarDate(date))
            return refuse(`${date} is not a calendar date`);
        return { kind: "day_page", method: "GET", url, competitionId, date };
    }
    const post = TECHNICAL_POST_PATH.exec(path);
    if (post !== null) {
        if (method !== "POST")
            return refuse("the technical form is only ever posted, with POST");
        const competitionId = writableCompetition(post[1]) ?? refuse(`competition ${post[1] ?? ""} is not 1916 or 1917`);
        return { kind: "technical_post", method: "POST", url, competitionId };
    }
    return refuse("it is neither of the two requests this build makes");
}
/**
 * What kind of page an ANSWER came from. Read, never requested: the core never follows or
 * re-requests the address an answer came from - it only ever re-reads the day page from the
 * address it builds itself.
 */
export function describeTmsResponseUrl(url) {
    const text = String(url).slice(0, 4096);
    const withoutOrigin = text.replace(/^[A-Za-z][A-Za-z0-9+.-]*:\/\/[^/?#]*/, "");
    const cut = withoutOrigin.search(/[?#]/);
    const path = cut < 0 ? withoutOrigin : withoutOrigin.slice(0, cut);
    let decoded = path;
    try {
        decoded = decodeURIComponent(path);
    }
    catch {
        // kept as written; the tests below still apply to it
    }
    const lowered = asciiLower(decoded);
    if (lowered.includes("publish"))
        return { kind: "publish", path };
    if (/(^|\/)(login|signin|sign-in)(\/|$)/.test(lowered))
        return { kind: "login", path };
    if (DAY_PAGE_PATH.test(path))
        return { kind: "day_page", path };
    if (TECHNICAL_POST_PATH.test(path))
        return { kind: "technical_post", path };
    return { kind: "other", path };
}
const ERROR_CLASS_MARKERS = ["alert-danger", "alert-error", "invalid-feedback"];
/** A dismissible alert starts with its close button's "x" sign (U+00D7), which is not TMS's words. */
const CLOSE_BUTTON_SIGN = new RegExp(`^${String.fromCharCode(0xd7)}[\\t\\n\\f\\r ]*`);
const TOAST_ERROR_CALLS = ["toastr.error(", "toastr['error'](", 'toastr["error"]('];
function isErrorElement(_tag, classes) {
    return classes.some((token) => {
        const lowered = asciiLower(token);
        return ERROR_CLASS_MARKERS.some((marker) => lowered.includes(marker));
    });
}
function readTmsPage(html) {
    return readHtmlPage(html, { collectTextOf: isErrorElement });
}
function sentenceAround(chunks, phrase) {
    for (const chunk of chunks) {
        const at = asciiLower(chunk).indexOf(phrase);
        if (at < 0)
            continue;
        let start = at;
        while (start > 0 && at - start < 300 && !/[.!?"'<>\n]/.test(chunk[start - 1] ?? ""))
            start -= 1;
        let end = at + phrase.length;
        while (end < chunk.length && end - at < 300 && !/[.!?"'<\n]/.test(chunk[end] ?? ""))
            end += 1;
        return fragment(chunk.slice(start, end));
    }
    return null;
}
/** The first string argument of every `toastr.error(...)` call in the page's scripts. */
function scriptErrorToasts(scripts) {
    const found = [];
    for (const script of scripts) {
        for (const call of TOAST_ERROR_CALLS) {
            let at = script.indexOf(call);
            while (at >= 0 && found.length < 10) {
                let i = at + call.length;
                while (i < script.length && /\s/.test(script[i] ?? ""))
                    i += 1;
                const quote = script[i];
                let text = "";
                if (quote === '"' || quote === "'" || quote === "`") {
                    let end = i + 1;
                    while (end < script.length && script[end] !== quote && text.length < 400) {
                        if (script[end] === "\\" && end + 1 < script.length) {
                            text += script[end + 1] ?? "";
                            end += 2;
                            continue;
                        }
                        text += script[end] ?? "";
                        end += 1;
                    }
                }
                const words = fragment(text);
                found.push(words === "" ? "an error message this build cannot read" : words);
                at = script.indexOf(call, at + call.length);
            }
        }
    }
    return found;
}
function messagesOf(page) {
    const texts = new Set();
    for (const element of page.collected) {
        const text = fragment(element.text.replace(CLOSE_BUTTON_SIGN, ""));
        if (text !== "")
            texts.add(text);
    }
    // An alert holding a nested error element reports both; keep the outer, fuller one.
    const all = [...texts];
    const errors = all.filter((text) => !all.some((other) => other !== text && other.includes(text))).slice(0, 10);
    // Chunk by chunk: a tag ends a sentence, so a toast cannot run on into the next element's text.
    const chunks = [...page.text, ...page.scriptText];
    return {
        errors,
        scriptErrors: [...new Set(scriptErrorToasts(page.scriptText))].slice(0, 10),
        guardText: sentenceAround(chunks, "cannot be changed"),
        saved: sentenceAround(chunks, asciiLower(TMS_TECHNICAL_SAVED_TEXT)),
    };
}
/** Read a TMS page's notices: its error alerts, its scripts' error toasts, its guard sentence, its toast. */
export function readTmsPageMessages(html) {
    return messagesOf(readTmsPage(html));
}
const NO_MESSAGES = { errors: [], scriptErrors: [], guardText: null, saved: null };
/** Error alerts and error toasts on `page` that were not already on `baseline` - page furniture is not an answer. */
function newAlerts(page, baseline) {
    const known = new Set([...baseline.errors, ...baseline.scriptErrors]);
    return [...new Set([...page.errors, ...page.scriptErrors])].filter((text) => !known.has(text));
}
function formTargetsLogin(form) {
    return form.action !== null && describeTmsResponseUrl(form.action.trim()).kind === "login";
}
/* -- Names ------------------------------------------------------------------------------------ */
/** Apostrophes join a name's parts ("O'Neill" = "ONeill"); every other mark separates words. */
const APOSTROPHES = new RegExp(`['\`${String.fromCharCode(0x2018, 0x2019, 0x02bc)}]`, "g");
function nameWords(text) {
    return String(text).normalize("NFKD").replace(/\p{M}+/gu, "").replace(APOSTROPHES, "").toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
}
/**
 * Does TMS's own label for a person name the official our plan names? The rule, strict about
 * content and tolerant of order and formatting:
 *
 *  * both are reduced to words - letters and digits only, accents removed, case ignored;
 *    apostrophes join ("O'NEILL" = "ONeill"), every other mark separates ("Ji-hyun" = "Ji Hyun");
 *  * EVERY word of our name must appear, whole, among the words of TMS's label. Order is free
 *    ("KIM Min" = "Min KIM"), and TMS's label may carry more (a middle name, a country code), but
 *    no word may be missing or partial: "KIM" never matches "KIMURA", an initial never stands for
 *    a name, and a different transliteration ("Mueller" for "Muller") does not match;
 *  * our name must hold at least one word with a letter.
 *
 * It is a cross-check that the person id we send is the person we mean. A mismatch refuses the
 * push: sending an id that TMS names differently would put someone else on the match.
 */
export function tmsNameMatches(label, mediaName) {
    const wanted = nameWords(mediaName);
    if (!wanted.some((word) => /\p{L}/u.test(word)))
        return false;
    const have = new Set(nameWords(label));
    return wanted.every((word) => have.has(word));
}
const SLOT_FIELD = /^m_([0-9]+)_(SJ|TJ|TO)$/;
const PUBLISH_FIELD = /^m_([0-9]+)_pub_technical$/;
const PERSON_ID = /^[0-9]+$/;
/** The fields this build reads or writes, spelled exactly as TMS spells them. */
const RECOGNISED_FIELD = /^(?:_token|date|m_[1-9][0-9]*_(?:SJ|TJ|TO|pub_technical))$/;
/** Keys an unfamiliar field must never reach TMS's server as. */
const TAKEN_PHP_KEY = /^(?:_token|date)$|^m_[0-9]+_/;
/** PHP's default `max_input_vars`: past it, PHP drops fields without a word. */
const PHP_MAX_INPUT_VARS = 1000;
/** A kick-off time at the start of the Time cell: "13:00:00", "13:00", "1:00 PM". */
const KICKOFF_TIME = /^[0-9]{1,2}:[0-9]{2}(?::[0-9]{2})?(?:[\t\n\f\r ]*[AaPp]\.?[Mm]\.?)?/;
/** "Official" as a word, where letters (not digits) are what make a word boundary. */
const OFFICIAL_WORD = /(?:^|[^A-Za-z])official(?:[^A-Za-z]|$)/i;
function actionTarget(action) {
    const trimmed = action.replace(/^[\t\n\f\r ]+|[\t\n\f\r ]+$/g, "");
    const absolute = /^(https?:)?\/\/([^/?#]*)/i.exec(trimmed);
    let rest = trimmed;
    let origin = null;
    if (absolute !== null) {
        origin = asciiLower(`${absolute[1] ?? "https:"}//${absolute[2] ?? ""}`);
        rest = trimmed.slice(absolute[0].length) || "/";
    }
    // A relative action resolves against the day page, so it can never reach the technical path.
    if (!rest.startsWith("/"))
        return null;
    const cut = rest.search(/[?#]/);
    return { origin, path: cut < 0 ? rest : rest.slice(0, cut), query: cut >= 0 && rest[cut] === "?" };
}
/**
 * The names PHP gives a submitted field. PHP drops leading spaces, turns "." and " " into "_",
 * and stops the name at "[" (an array); an unmatched "[" becomes "_" and the rest is kept as
 * written (PHP 7) or folded the same way (PHP 8). Both spellings are returned when they differ.
 */
function phpKeys(name) {
    const trimmed = name.replace(/^ +/, "");
    let base = "";
    for (let i = 0; i < trimmed.length; i += 1) {
        const c = trimmed[i] ?? "";
        if (c === " " || c === ".") {
            base += "_";
            continue;
        }
        if (c === "[") {
            if (trimmed.indexOf("]", i + 1) >= 0)
                return { keys: [base], array: true };
            const rest = trimmed.slice(i + 1);
            return { keys: [...new Set([`${base}_${rest}`, `${base}_${rest.replace(/[ .[]/g, "_")}`])], array: false };
        }
        base += c;
    }
    return { keys: [base], array: false };
}
/**
 * Refuse a form whose fields would reach TMS's server differently from how we read them: two
 * fields PHP would fold into one (the last would win), or an unfamiliar field PHP would read as
 * `_token`, `date` or a per-match field - such as `m.22884.SJ` or `m_22900_pub.technical`.
 */
function phpFieldProblem(entries) {
    if (entries.length > PHP_MAX_INPUT_VARS) {
        return `the form would send ${String(entries.length)} fields, more than PHP reads (${String(PHP_MAX_INPUT_VARS)}), so TMS would silently drop some`;
    }
    const groups = new Map();
    for (const entry of entries) {
        const { keys, array } = phpKeys(entry.name);
        const recognised = RECOGNISED_FIELD.test(entry.name);
        for (const key of keys) {
            if (key === "")
                continue; // PHP ignores a field with no name
            if (!recognised && TAKEN_PHP_KEY.test(key)) {
                return `the form carries a field "${fragment(entry.name)}" that TMS's server would read as "${fragment(key)}", a field this build reads or writes`;
            }
            const group = groups.get(key) ?? { names: [], scalar: false };
            group.names.push(entry.name);
            if (!array)
                group.scalar = true;
            groups.set(key, group);
            if (group.names.length > 1 && group.scalar) {
                const names = group.names.slice(0, 2).map((field) => `"${fragment(field)}"`).join(" and ");
                return `the fields ${names} reach TMS's server as the same field "${fragment(key)}", so only the last would count`;
            }
        }
    }
    return null;
}
function rowStatus(page, controls) {
    const rows = new Set(controls.map((control) => control.row));
    const row = [...rows][0];
    if (rows.size !== 1 || row === null || row === undefined)
        return { problem: "its fields are not all in one table row" };
    const table = page.tables[row.table];
    if (table === undefined)
        return { problem: "its row is not in a table" };
    if (table.hasRowspan)
        return { problem: "its table merges rows (rowspan), so its columns cannot be matched to the header" };
    let column = null;
    for (const candidate of table.rows) {
        const header = candidate.cells.find((cell) => asciiLower(cell.text) === "time");
        if (header !== undefined) {
            column = header.column;
            break;
        }
    }
    if (column === null)
        return { problem: "its table has no Time column" };
    const at = column;
    const cell = row.cells.find((candidate) => candidate.column <= at && at < candidate.column + candidate.colspan);
    if (cell === undefined)
        return { problem: "its row has no Time cell" };
    // The status is whatever follows the kick-off time; non-breaking spaces count as spaces.
    const text = cell.text.replace(/\s+/g, " ").trim();
    const status = text.replace(KICKOFF_TIME, "").replace(/\s+/g, " ").trim();
    return { status: fragment(status), official: OFFICIAL_WORD.test(status) };
}
/**
 * TMS's own words for a match row ({@link TmsTechnicalRowText}). Strict about where they are and
 * never a reason to refuse: the table must name exactly one Time and one Match column, the row must
 * span exactly the columns of the header row (a row that lost or gained a cell has shifted), and it
 * must hold exactly one single-column cell under each, with no form field in it; otherwise that
 * value is null.
 */
function rowText(page, controls) {
    const none = { kickOff: null, matchText: null };
    const rows = new Set(controls.map((control) => control.row));
    const row = [...rows][0];
    if (rows.size !== 1 || row === null || row === undefined)
        return none;
    const table = page.tables[row.table];
    if (table === undefined || table.hasRowspan)
        return none;
    const width = (cells) => cells.reduce((sum, cell) => sum + cell.colspan, 0);
    const cellUnder = (title) => {
        const found = table.rows.flatMap((candidate) => candidate.cells.filter((cell) => asciiLower(cell.text) === title).map((cell) => ({ cell, owner: candidate })));
        const header = found[0];
        if (found.length !== 1 || header === undefined || header.cell.colspan !== 1 || header.owner === row)
            return null;
        if (width(row.cells) !== width(header.owner.cells))
            return null;
        const under = row.cells.filter((cell) => cell.column <= header.cell.column && header.cell.column < cell.column + cell.colspan);
        const cell = under[0];
        if (under.length !== 1 || cell === undefined || cell.colspan !== 1)
            return null;
        return page.controls.some((control) => control.row === row && control.column === cell.column) ? null : cell;
    };
    const time = cellUnder("time");
    const kick = time === null ? null : KICKOFF_TIME.exec(time.text.replace(/\s+/g, " ").trim());
    const kickOff = kick === null ? "" : fragment(kick[0]);
    const match = cellUnder("match");
    const matchText = match === null ? "" : fragment(match.text);
    return { kickOff: kickOff === "" ? null : kickOff, matchText: matchText === "" ? null : matchText };
}
function refusedRead(reason, detail, advice = null, warnings = []) {
    return { kind: "refused", reason, detail, advice, warnings };
}
/**
 * Parse TMS's day page into what a browser would submit and what each match currently holds -
 * or refuse. It is strict: a form it cannot account for in full is a refusal, never a guess.
 *
 * Refusals: `session_expired` for a sign-in page (a password input anywhere, or a sign-in form
 * and no technical form); `form_changed` for everything else it cannot read - a page too large
 * or too deep, a CDATA section it cannot follow, no form posting to
 * `/admin/content/competitions/{comp}/technical` or more than one, a missing or empty `_token`,
 * a missing or different `date`, a field PHP would fold into one of ours, a match with only some
 * of its selects, a duplicated field, a slot that does not mark exactly one option `selected`, an
 * option that is not a person id, a row whose status and lock disagree, an editable row without
 * an enabled publish box, or no matches at all.
 */
export function parseTechnicalDayPage(html, expected) {
    requireCompetitionDigits(expected.competitionId, "parseTechnicalDayPage");
    requireCalendarDate(expected.date, "parseTechnicalDayPage");
    const expectedOrigin = expected.base === undefined ? null : asciiLower(requireHttpsOrigin(expected.base, "parseTechnicalDayPage"));
    const page = readTmsPage(String(html));
    const where = `TMS competition ${expected.competitionId} on ${expected.date}`;
    const sessionEnded = (why) => refusedRead("session_expired", `TMS answered with its sign-in page instead of the technical-officials form for ${where} (${why}), so the TMS session has ended.`);
    const changed = (why, advice = null) => refusedRead("form_changed", `The TMS technical-officials form for ${where} could not be read in full: ${why}.`, advice);
    const problem = page.problems[0];
    if (problem !== undefined)
        return changed(`the page ${fragment(problem)}`);
    if (page.passwordInputs > 0)
        return sessionEnded("the page asks for a password");
    const technicalPath = `/admin/content/competitions/${expected.competitionId}/technical`;
    const liveForms = page.forms.filter((form) => !form.inTemplate);
    const technical = liveForms.filter((form) => form.action !== null && actionTarget(form.action)?.path === technicalPath);
    if (technical.length === 0) {
        if (liveForms.some(formTargetsLogin))
            return sessionEnded("the page carries a sign-in form and no technical-officials form");
        const elsewhere = liveForms
            .map((form) => (form.action === null ? null : actionTarget(form.action)?.path ?? null))
            .filter((path) => path !== null && TECHNICAL_POST_PATH.test(path));
        const note = elsewhere.length > 0 ? ` (a form posts to ${fragment(elsewhere.join(" and "))} instead)` : "";
        return changed(`no form on the page posts to ${technicalPath}${note}`);
    }
    if (technical.length > 1)
        return changed(`${String(technical.length)} forms on the page post to ${technicalPath}`);
    const form = technical[0];
    const target = actionTarget(form.action ?? "");
    if (target === null)
        return changed("the technical form's address cannot be read");
    if (form.attributes.some((attribute) => ["action", "method", "enctype", "accept-charset"].includes(attribute.name) && !attribute.certain)) {
        return changed("the technical form's attributes hold a character reference this build cannot decode for certain");
    }
    if (target.query)
        return changed("the technical form posts to its address with a query string added");
    if (target.origin !== null && expectedOrigin !== null && target.origin !== expectedOrigin) {
        return changed(`the technical form posts to ${fragment(target.origin)}, not to ${fragment(expectedOrigin)}`);
    }
    if (form.method !== "post")
        return changed(`the technical form is sent with ${form.method.toUpperCase()}, not POST`);
    if (form.enctype !== "application/x-www-form-urlencoded")
        return changed(`the technical form is sent as ${form.enctype}, not as a plain form post`);
    if (form.acceptCharset !== null) {
        const charsets = form.acceptCharset.split(/[\t\n\f\r ]+/).filter((token) => token !== "").map(asciiLower);
        if (charsets.length > 0 && !charsets.every((charset) => charset === "utf-8" || charset === "utf8")) {
            return changed(`the technical form declares the character set "${fragment(form.acceptCharset)}", not UTF-8`);
        }
    }
    const listed = formEntryList(page, form);
    if (!listed.ok)
        return changed(fragment(listed.problem));
    const entries = listed.entries;
    const owned = page.controls.filter((control) => control.form === form && !control.inTemplate);
    const hiddenField = (name, label) => {
        const controls = owned.filter((control) => control.name === name);
        if (controls.length === 0)
            return { problem: `${label} is missing` };
        if (controls.length > 1)
            return { problem: `${label} appears ${String(controls.length)} times` };
        const control = controls[0];
        if (control.tag !== "input" || control.type !== "hidden")
            return { problem: `${label} is not a hidden field` };
        const sent = entries.filter((entry) => entry.name === name);
        if (sent.length !== 1 || sent[0] === undefined)
            return { problem: `${label} would not be sent` };
        return { value: sent[0].value };
    };
    const token = hiddenField("_token", "the form's security token (_token)");
    if ("problem" in token)
        return changed(token.problem);
    if (token.value === "")
        return changed("the form's security token (_token) is empty");
    const date = hiddenField("date", "the form's date field");
    if ("problem" in date)
        return changed(date.problem);
    if (date.value !== expected.date)
        return changed(`the form is for "${fragment(date.value)}", not ${expected.date}`);
    const byMatch = new Map();
    for (const control of owned) {
        const slot = SLOT_FIELD.exec(control.name);
        const publish = slot === null ? PUBLISH_FIELD.exec(control.name) : null;
        const digits = (slot ?? publish)?.[1];
        if (digits === undefined)
            continue;
        if (!/^[1-9][0-9]*$/.test(digits) || !Number.isSafeInteger(Number(digits))) {
            return changed(`the field ${fragment(control.name)} names a match id this build cannot read`);
        }
        let fields = byMatch.get(digits);
        if (fields === undefined) {
            fields = { slots: {}, publish: null };
            byMatch.set(digits, fields);
        }
        if (slot !== null) {
            const slotName = slot[2];
            if (fields.slots[slotName] !== undefined)
                return changed(`the field ${control.name} appears more than once`);
            if (control.tag !== "select")
                return changed(`the field ${control.name} is not a select`);
            fields.slots[slotName] = control;
        }
        else {
            if (fields.publish !== null)
                return changed(`the field ${control.name} appears more than once`);
            if (control.tag !== "input" || control.type !== "checkbox")
                return changed(`the field ${control.name} is not a checkbox`);
            fields.publish = control;
        }
    }
    if (byMatch.size === 0)
        return changed("the form holds no matches");
    const matches = [];
    for (const [digits, fields] of byMatch) {
        const id = Number(digits);
        const label = `match ${String(id)}`;
        const missing = TMS_APPOINTMENT_PUSH_SLOTS.filter((slot) => fields.slots[slot] === undefined);
        if (missing.length > 0) {
            const present = TMS_APPOINTMENT_PUSH_SLOTS.filter((slot) => fields.slots[slot] !== undefined);
            const has = present.length === 0 ? "a publish box" : `${present.join(" and ")} select${present.length === 1 ? "" : "s"}`;
            return changed(`${label} has ${has} but no ${missing.join(" or ")} select`);
        }
        const selects = TMS_APPOINTMENT_PUSH_SLOTS.map((slot) => fields.slots[slot]);
        const locked = selects.filter((control) => control.disabled).length;
        if (locked !== 0 && locked !== selects.length)
            return changed(`${label} has some technical selects locked and some not`);
        const editable = locked === 0;
        const slots = {};
        for (const slot of TMS_APPOINTMENT_PUSH_SLOTS) {
            const control = fields.slots[slot];
            const strange = control.options.find((option) => !PERSON_ID.test(option.value));
            if (strange !== undefined) {
                return changed(`the ${slot} select of ${label} offers "${fragment(strange.value)}", which is not a TMS person id`);
            }
            // The held official must be STATED: exactly one option marked selected. Without it a
            // browser falls back to the first option ("0") and a whole-day save would clear the slot.
            const marked = control.options.filter((option) => option.selectedAttribute).length;
            if (marked !== 1) {
                return changed(`TMS's page does not state which official each slot holds, so a whole-day save could not keep them: the ${slot} ` +
                    `select of ${label} marks ${marked === 0 ? "no option" : `${String(marked)} options`} as selected`, `Open the day in TMS and look at ${label}: if its ${slot} shows an official, that official is no longer in the ` +
                    "list - correct the slot in TMS, then press again. If the slot looks right, TMS's page has changed: enter the " +
                    "day by hand and tell the build team.");
            }
            const chosen = control.options[control.selectedIndex];
            if (chosen === undefined)
                return changed(`the ${slot} select of ${label} has no selected option`);
            if (editable && chosen.disabled) {
                return changed(`the official TMS holds in the ${slot} select of ${label} can no longer be chosen there (their entry is disabled), so a save from this page would clear the slot`, `Correct the ${slot} of ${label} in TMS by hand, then press again.`);
            }
            const options = [];
            const unavailable = [];
            for (const option of control.options) {
                if (option.value === "0")
                    continue;
                (option.disabled ? unavailable : options).push({ personId: option.value, label: fragment(option.label) });
            }
            slots[slot] = {
                fieldName: control.name,
                current: chosen.value === "0" ? null : chosen.value,
                currentLabel: fragment(chosen.label),
                offered: [...new Set(options.map((option) => option.personId))],
                options,
                unavailable,
            };
        }
        // Two signals must agree before we believe TMS will accept an edit: the locked selects and the
        // row's own status. And the publish box must be locked exactly when the selects are.
        const box = fields.publish;
        const status = rowStatus(page, box === null ? selects : [...selects, box]);
        if ("problem" in status)
            return changed(`the status of ${label} cannot be read: ${status.problem}`);
        if (status.official && editable)
            return changed(`the row of ${label} says "${status.status}", but its selects can still be changed`);
        if (!status.official && !editable)
            return changed(`the selects of ${label} are locked, but its row says "${status.status}", not Official`);
        if (box === null && editable)
            return changed(`${label} can be edited but has no publish box, so a save could unpublish it`);
        if (box !== null && box.disabled !== !editable) {
            return changed(editable
                ? `the publish box of ${label} is locked while its crew can be edited, so a save would unpublish it`
                : `${label} is locked as Official, but its publish box can still be changed`);
        }
        matches.push({
            tmsMatchDbId: id,
            slots: slots,
            editable,
            official: status.official,
            status: status.status,
            published: box === null ? null : box.checked,
            tmsRow: rowText(page, selects),
        });
    }
    // Last, once every field is accounted for by name: how TMS's server will actually read them.
    const php = phpFieldProblem(entries);
    if (php !== null)
        return changed(php);
    return {
        kind: "snapshot",
        snapshot: {
            competitionId: expected.competitionId,
            date: expected.date,
            form: { action: fragment(form.action ?? ""), method: "post" },
            token: token.value,
            entries,
            matches,
            messages: messagesOf(page),
        },
    };
}
/**
 * Read a whole day-page ANSWER: its shape, status and address first, then its markup.
 *
 * An opaque answer (status 0) or a readable redirect means TMS sent the day page elsewhere -
 * almost always to its sign-in page - so it is `session_expired`. An answer from a publish or
 * unpublish address is refused AND carries a loud warning, since reaching one may have changed
 * the day's publish state.
 */
export function readTechnicalDayResponse(response, expected) {
    const where = `TMS competition ${expected.competitionId} on ${expected.date}`;
    const malformed = tmsHttpResponseProblem(response);
    if (malformed !== null) {
        return refusedRead("transport_unavailable", `The TMS Push transport gave a malformed answer for the day page of ${where} (${malformed}).`, "Press again; if it persists, enter the day in TMS by hand and report it to the build team.");
    }
    if (response.status === 0 || (response.status >= 300 && response.status < 400)) {
        return refusedRead("session_expired", `TMS redirected the day page for ${where} instead of showing it (the redirect was not followed) - almost always to its sign-in page, so the TMS session has most likely ended.`);
    }
    const answeredFrom = describeTmsResponseUrl(response.url);
    if (answeredFrom.kind === "publish") {
        return refusedRead("form_changed", `The day page for ${where} was answered from a TMS publish or unpublish address (${fragment(answeredFrom.path)}), which this build never requests.`, "Check the day's publish state in TMS by eye, and report this to the build team.", [
            `A read of the day page ended at a TMS publish or unpublish address (${fragment(answeredFrom.path)}), so the day's publish state may have changed. Check it in TMS by eye.`,
        ]);
    }
    if (answeredFrom.kind === "login" || response.status === 401 || response.status === 419) {
        return refusedRead("session_expired", `TMS answered the day page for ${where} with its sign-in page, so the TMS session has ended.`);
    }
    if (response.status === 404) {
        return refusedRead("rejected_by_tms", `TMS has no day page for ${where} (HTTP 404).`, "Check in TMS that this competition has matches on this day, then press again.");
    }
    if (response.status < 200 || response.status >= 300) {
        return refusedRead("rejected_by_tms", `TMS answered HTTP ${String(response.status)} when the day page for ${where} was read.`);
    }
    if (answeredFrom.kind !== "day_page") {
        return refusedRead("form_changed", `TMS answered the day page for ${where} from ${fragment(answeredFrom.path || "an unknown address")}, not from the day page itself.`);
    }
    const expectedPath = `/admin/content/competitions/${expected.competitionId}/appointments/${expected.date}`;
    if (answeredFrom.path !== expectedPath) {
        return refusedRead("form_changed", `TMS answered the day page for ${where} from ${fragment(answeredFrom.path)}, which is a different day or competition.`);
    }
    return parseTechnicalDayPage(response.body, expected);
}
function crewOf(match) {
    return { TO: match.slots.TO.current, SJ: match.slots.SJ.current, TJ: match.slots.TJ.current };
}
function crewText(crew) {
    return TMS_APPOINTMENT_PUSH_SLOTS.map((slot) => `${slot} ${crew[slot] ?? "none"}`).join(" - ");
}
function matchLabel(payload) {
    return `${appointmentCompetitionWord(payload.competitionGender)} ${fragment(payload.displayMatchNo)} (TMS match ${String(payload.tmsMatchDbId)})`;
}
function dayLabel(payloads, competitionId, date) {
    const first = payloads[0];
    const word = first === undefined ? "" : `${appointmentCompetitionWord(first.competitionGender)} `;
    return `the ${word}technical officials of ${date} (TMS competition ${competitionId})`;
}
/** What TMS says about one planned slot: its label for the person, or why it cannot be sent. */
function checkPlannedSlot(state, slot, official, matchText) {
    const id = official.tmsPersonId;
    const who = `${fragment(official.mediaName)} (TMS person ${id})`;
    const offered = state.options.filter((option) => option.personId === id);
    if (offered.length === 0) {
        const listed = state.unavailable.find((option) => option.personId === id);
        if (listed !== undefined) {
            return {
                label: listed.label,
                problem: `the ${slot} of ${matchText} is ${who}, whom TMS lists as "${listed.label}" in that select but does not let anyone ` +
                    "choose (the entry is disabled). Choose another official, or clear what blocks them in TMS, then press again.",
            };
        }
        return {
            label: null,
            problem: `the ${slot} of ${matchText} is ${who}, who is not among the officials TMS offers in that select. Check that they ` +
                "are registered for this competition in TMS, or choose another official, then press again.",
        };
    }
    const labels = [...new Set(offered.map((option) => option.label))];
    const label = labels[0] ?? "";
    if (labels.length > 1) {
        return {
            label,
            problem: `TMS offers person ${id} twice under different names (${quoted(labels)}) in the ${slot} select of ${matchText}. Check the official in TMS, then press again.`,
        };
    }
    if (!tmsNameMatches(label, official.mediaName)) {
        return {
            label,
            problem: `the ${slot} of ${matchText} is ${who}, but TMS's own name for person ${id} in that select is "${label}". The person id ` +
                "and the name disagree, so nothing may be sent. Check the official's TMS person id in the Control Centre, then press again.",
        };
    }
    return { label, problem: null };
}
/**
 * Plan the one post for a competition-day: the browser's own list with only the planned slots
 * put in - or a refusal BEFORE anything is sent.
 *
 *  * A planned match missing from the form refuses the whole day (`form_changed`).
 *  * A planned official TMS does not offer in that select - or whose TMS label does not name the
 *    official we planned ({@link tmsNameMatches}) - refuses the whole day (`form_changed`), naming
 *    the match, the slot and the official.
 *  * A planned match TMS has locked (Official) is not sent and not touched; it is listed in
 *    `notPushed` with TMS's own rule (`rejected_by_tms`), and the rest still go.
 *  * A planned match whose publish box is ticked is treated per `options.publishedMatches`
 *    (default "refuse": listed in `notPushed`, `transport_unavailable`, sent back as held).
 *  * If nothing is left to send, that is a refusal too.
 *
 * Payloads of the wrong shape, for another competition or day, a repeated match or no payloads at
 * all are programmer errors, and throw.
 */
export function planTechnicalDayPost(snapshot, payloads, options = {}) {
    validateTechnicalPayloads(payloads, snapshot.competitionId, snapshot.date, "planTechnicalDayPost");
    const policy = options.publishedMatches ?? "refuse";
    if (policy !== "refuse" && policy !== "allow")
        throw new Error(`planTechnicalDayPost: publishedMatches must be "refuse" or "allow".`);
    const day = dayLabel(payloads, snapshot.competitionId, snapshot.date);
    const byId = new Map(snapshot.matches.map((match) => [match.tmsMatchDbId, match]));
    const notPushed = [];
    const planned = new Map();
    const pushed = [];
    const refuse = (reason, detail) => ({ kind: "refused", reason, detail, notPushed });
    for (const payload of payloads) {
        const matchText = matchLabel(payload);
        const match = byId.get(payload.tmsMatchDbId);
        if (match === undefined) {
            return refuse("form_changed", `Nothing was sent to TMS for ${day}: ${matchText} is not on TMS's technical-officials form for that day. ` +
                "TMS may have moved or removed it. Check the day in TMS before pressing again.");
        }
        if (!match.editable) {
            notPushed.push({
                tmsMatchDbId: payload.tmsMatchDbId,
                reason: "rejected_by_tms",
                detail: `Not sent: ${matchText} is ${fragment(match.status)} in TMS, and TMS's own rule is "${TMS_OFFICIAL_MATCH_RULE}". ` +
                    "Its crew in TMS was left exactly as it was. Remove it from the push, or change it in TMS by hand.",
            });
            continue;
        }
        if (match.published === true && policy === "refuse") {
            notPushed.push({
                tmsMatchDbId: payload.tmsMatchDbId,
                reason: "transport_unavailable",
                detail: `Not sent: ${matchText}. ${TMS_PUBLISHED_MATCH_RULE} Its crew in TMS was left exactly as it was.`,
            });
            continue;
        }
        const labels = {};
        for (const slot of TMS_APPOINTMENT_PUSH_SLOTS) {
            const official = payload.slots[slot];
            const check = checkPlannedSlot(match.slots[slot], slot, official, matchText);
            if (check.problem !== null)
                return refuse("form_changed", `Nothing was sent to TMS for ${day}: ${check.problem}`);
            labels[slot] = check.label ?? "";
            planned.set(match.slots[slot].fieldName, official.tmsPersonId);
        }
        pushed.push({
            tmsMatchDbId: payload.tmsMatchDbId,
            sent: { TO: payload.slots.TO.tmsPersonId, SJ: payload.slots.SJ.tmsPersonId, TJ: payload.slots.TJ.tmsPersonId },
            labels: labels,
            before: crewOf(match),
            published: match.published,
        });
    }
    if (pushed.length === 0) {
        const first = notPushed[0];
        return refuse(first?.reason ?? "rejected_by_tms", `Nothing was sent to TMS for ${day}: no match in this push can be changed from here (${notPushed.length === 1 ? "it is" : "they are"} ` +
            "Official or already published in TMS). Change them in TMS by hand, or unpublish a published one there first.");
    }
    // The browser's list, in its order, with only our fields put in. Nothing added, nothing dropped.
    const substituted = new Map();
    const entries = snapshot.entries.map((entry) => {
        const value = planned.get(entry.name);
        if (value === undefined)
            return entry;
        substituted.set(entry.name, (substituted.get(entry.name) ?? 0) + 1);
        return { name: entry.name, value };
    });
    for (const field of planned.keys()) {
        if (substituted.get(field) !== 1) {
            return refuse("form_changed", `Nothing was sent to TMS for ${day}: the field ${fragment(field)} would not be sent exactly once by the form as TMS ` +
                "rendered it. Enter this day's appointments in TMS by hand, and tell the build team that TMS's page has changed.");
        }
    }
    const pushedIds = new Set(pushed.map((match) => match.tmsMatchDbId));
    return {
        kind: "post",
        body: encodeFormUrlEncoded(entries),
        entries,
        expected: {
            competitionId: snapshot.competitionId,
            date: snapshot.date,
            pushed,
            untouched: snapshot.matches
                .filter((match) => !pushedIds.has(match.tmsMatchDbId))
                .map((match) => ({ tmsMatchDbId: match.tmsMatchDbId, crew: crewOf(match) })),
            publishFlags: snapshot.matches.map((match) => ({ tmsMatchDbId: match.tmsMatchDbId, published: match.published })),
        },
        notPushed,
    };
}
export function classifyTechnicalPostResponse(response, baseline = NO_MESSAGES) {
    const malformed = tmsHttpResponseProblem(response);
    if (malformed !== null)
        return { kind: "unknown", detail: `TMS's answer to the save could not be read: ${malformed}.` };
    const failed = (reason, definite, detail) => ({
        kind: "failed",
        reason,
        definite,
        detail,
    });
    const status = response.status;
    if (status === 0) {
        return {
            kind: "unknown",
            detail: "TMS answered the save with a redirect that was not followed, so only the read-back can tell what it saved.",
        };
    }
    if (status === 419) {
        return failed("session_expired", true, "TMS answered the save with 419 Page Expired: the form's security token was stale or the TMS session had ended, " +
            "so TMS refused the save before reading it. It was not retried. Sign in to TMS again, then press again.");
    }
    const answeredFrom = describeTmsResponseUrl(response.url);
    const page = readTmsPage(response.body);
    if (answeredFrom.kind === "login" || status === 401 || page.passwordInputs > 0 || page.forms.some(formTargetsLogin)) {
        return failed("session_expired", true, "TMS answered the save with its sign-in page, so the TMS session had ended and the save was refused. It was not " +
            "retried. Sign in to TMS again, then press again.");
    }
    if (answeredFrom.kind === "publish") {
        return failed("rejected_by_tms", false, `TMS's answer to the save ended at a publish or unpublish address (${fragment(answeredFrom.path)}). This build never ` +
            "requests one, but the transport followed a redirect into it, so the day's publish state may have changed.");
    }
    if (status >= 300 && status < 400) {
        return {
            kind: "unknown",
            detail: `TMS answered the save with a redirect (HTTP ${String(status)}) that was not followed, so only the read-back can tell what it saved.`,
        };
    }
    if (status >= 500) {
        return failed("rejected_by_tms", false, `TMS answered the save with HTTP ${String(status)}, a server error, so its answer does not say whether anything was saved.`);
    }
    if (status < 200 || status >= 300) {
        return failed("rejected_by_tms", status >= 400, `TMS refused the save with HTTP ${String(status)}. Check that the day can still be edited in TMS, then press again.`);
    }
    if (page.problems.length > 0) {
        return { kind: "unknown", detail: "TMS's answer to the save could not be read in full, so only the read-back can tell what it saved." };
    }
    const messages = messagesOf(page);
    const from = response.redirected && answeredFrom.kind !== "day_page" ? ` (its answer came from ${fragment(answeredFrom.path || "an unknown address")})` : "";
    const alerts = newAlerts(messages, baseline);
    if (alerts.length > 0) {
        const also = messages.saved === null ? "" : ` It also showed "${messages.saved}", which does not outweigh the error.`;
        return failed("rejected_by_tms", true, `TMS answered the save with an error: ${quoted(alerts)}${from}.${also} Correct what TMS objected to, then press again.`);
    }
    if (messages.saved !== null)
        return { kind: "saved", confirmed: true, detail: `TMS confirmed: "${messages.saved}"${from}.` };
    if (messages.guardText !== null && messages.guardText !== baseline.guardText) {
        return failed("rejected_by_tms", true, `TMS answered the save with: "${messages.guardText}"${from}. Remove any Official match from the push, or change it in TMS by hand.`);
    }
    return { kind: "saved", confirmed: false, detail: `TMS answered the save with HTTP ${String(status)} and no confirmation message${from}.` };
}
function publishWord(published) {
    return published === null ? "absent" : published ? "ticked" : "unticked";
}
/** The person ids a read showed for a match, as the API wants them: strings, "0" for none. */
export function tmsReadBackOf(snapshot, tmsMatchDbId) {
    const match = snapshot.matches.find((candidate) => candidate.tmsMatchDbId === tmsMatchDbId);
    if (match === undefined)
        return null;
    return { TO: match.slots.TO.current ?? "0", SJ: match.slots.SJ.current ?? "0", TJ: match.slots.TJ.current ?? "0" };
}
/**
 * Compare the read-back with what was sent, slot by slot, and with what was left alone.
 *
 * A pushed match is `accepted_draft` only when all three slots show exactly what was sent. Any
 * untouched match whose crew changed, any publish box that changed (Official rows included), any
 * match that vanished and any match that APPEARED since the first read is reported at day level,
 * in words a TD will not miss - whether this save or someone else did it.
 */
export function verifyTechnicalDay(expected, after) {
    if (after.competitionId !== expected.competitionId || after.date !== expected.date) {
        throw new Error(`verifyTechnicalDay: the read-back is competition ${after.competitionId} on ${after.date}, ` +
            `but the push was competition ${expected.competitionId} on ${expected.date}.`);
    }
    const shownById = new Map(after.matches.map((match) => [match.tmsMatchDbId, match]));
    const matches = expected.pushed.map((pushed) => {
        const id = String(pushed.tmsMatchDbId);
        const shown = shownById.get(pushed.tmsMatchDbId);
        if (shown === undefined) {
            return {
                tmsMatchDbId: pushed.tmsMatchDbId,
                outcome: "failed",
                reason: "rejected_by_tms",
                detail: `Match ${id} is no longer on TMS's technical-officials form for this day, so the crew that was sent cannot be seen in TMS (read back after the save). Check the day in TMS.`,
                differences: [],
                missing: true,
                readBack: null,
            };
        }
        const readBack = tmsReadBackOf(after, pushed.tmsMatchDbId);
        const differences = TMS_APPOINTMENT_PUSH_SLOTS.filter((slot) => shown.slots[slot].current !== pushed.sent[slot]).map((slot) => ({ slot, sent: pushed.sent[slot], shown: shown.slots[slot].current }));
        if (differences.length === 0) {
            const note = shown.published === true
                ? " Its publish box is ticked in TMS, so this crew is already public."
                : shown.published === false
                    ? " It stays private until the TD publishes it in TMS."
                    : "";
            return {
                tmsMatchDbId: pushed.tmsMatchDbId,
                outcome: "accepted_draft",
                detail: `TMS shows ${crewText(pushed.sent)} for match ${id} after the save (read back).${note}`,
                published: shown.published,
                readBack,
            };
        }
        return {
            tmsMatchDbId: pushed.tmsMatchDbId,
            outcome: "failed",
            reason: "rejected_by_tms",
            detail: `TMS does not show the crew that was sent for match ${id} (read back after the save): ` +
                `${differences.map((difference) => `${difference.slot} sent ${difference.sent}, TMS shows ${difference.shown ?? "none"}`).join("; ")}.`,
            differences,
            missing: false,
            readBack,
        };
    });
    const collateral = [];
    for (const untouched of expected.untouched) {
        const id = String(untouched.tmsMatchDbId);
        const shown = shownById.get(untouched.tmsMatchDbId);
        if (shown === undefined) {
            collateral.push(`Match ${id} was on the day when it was read but is missing from the read-back; this push sent no change for it. Check the day in TMS.`);
            continue;
        }
        const changed = TMS_APPOINTMENT_PUSH_SLOTS.filter((slot) => shown.slots[slot].current !== untouched.crew[slot]);
        if (changed.length > 0) {
            collateral.push(`Match ${id} was not changed by this push, but TMS now shows ` +
                `${changed.map((slot) => `${slot} ${shown.slots[slot].current ?? "none"} (was ${untouched.crew[slot] ?? "none"})`).join(", ")}. ` +
                `Someone else may be editing the day, or the save touched it. Check match ${id} in TMS.`);
        }
    }
    const known = new Set(expected.publishFlags.map((flag) => flag.tmsMatchDbId));
    for (const flag of expected.publishFlags) {
        const shown = shownById.get(flag.tmsMatchDbId);
        if (shown === undefined || shown.published === flag.published)
            continue;
        collateral.push(`The publish box of match ${String(flag.tmsMatchDbId)} changed from ${publishWord(flag.published)} to ` +
            `${publishWord(shown.published)} between the read and the read-back. This build never changes a publish box: ` +
            `check match ${String(flag.tmsMatchDbId)} in TMS and publish or unpublish it by hand if needed.`);
    }
    for (const match of after.matches) {
        if (known.has(match.tmsMatchDbId))
            continue;
        collateral.push(`Match ${String(match.tmsMatchDbId)} is on the day now but was not when it was read, so this push sent nothing for it: ` +
            `the save may have cleared its crew or unpublished it (TMS shows ${crewText(crewOf(match))}, publish box ` +
            `${publishWord(match.published)}). Check match ${String(match.tmsMatchDbId)} in TMS.`);
    }
    return { matches, collateral };
}
function instantOf(value) {
    if (value === null || typeof value !== "object")
        return null;
    const date = value;
    if (typeof date.getTime !== "function" || typeof date.toISOString !== "function")
        return null;
    const time = date.getTime();
    return Number.isFinite(time) ? date.toISOString() : null;
}
function safeInstant(clock, fallback) {
    try {
        return instantOf(clock()) ?? fallback;
    }
    catch {
        return fallback;
    }
}
/** Everything that can be wrong with the call itself is found here, before any request. */
function prepareRequest(transport, request, options, where) {
    if (transport === null || typeof transport !== "object")
        throw new Error(`${where}: there is no transport.`);
    const name = transport.name;
    if (typeof name !== "string" || name.trim() === "" || name.length > 100)
        throw new Error(`${where}: the transport has no name.`);
    const redirects = transport.redirects;
    if (redirects !== "manual" && redirects !== "follow")
        throw new Error(`${where}: the transport must say how it handles redirects ("manual" or "follow").`);
    if (typeof transport.getDayPage !== "function" || typeof transport.postTechnical !== "function") {
        throw new Error(`${where}: the transport cannot read the day page or post the technical form.`);
    }
    const { competitionId, date, payloads } = request;
    requireCompetitionDigits(competitionId, where);
    requireCalendarDate(date, where);
    const originOptions = options.testOnlyOrigin === true ? { testOnlyOrigin: true } : {};
    const origin = requirePinnedOrigin(transport.base, originOptions, where);
    validateTechnicalPayloads(payloads, competitionId, date, where);
    const policy = options.publishedMatches ?? "refuse";
    if (policy !== "refuse" && policy !== "allow")
        throw new Error(`${where}: publishedMatches must be "refuse" or "allow".`);
    // Every URL is built here, from the pinned origin, and passes the guard before it is used.
    const dayUrl = guardTmsTechnicalRequest("GET", tmsTechnicalDayPageUrl(origin, competitionId, date), origin, originOptions).url;
    const postUrl = guardTmsTechnicalRequest("POST", tmsTechnicalPostUrl(origin, competitionId), origin, originOptions).url;
    const clock = options.now ?? (() => new Date());
    let startAt;
    try {
        startAt = instantOf(clock());
    }
    catch (error) {
        throw new Error(`${where}: the clock failed (${fragment(errorMessage(error))}).`);
    }
    if (startAt === null)
        throw new Error(`${where}: the clock did not give a valid time.`);
    return {
        transport,
        name,
        redirects,
        competitionId,
        date,
        payloads,
        origin,
        originOptions,
        policy,
        dayUrl,
        postUrl,
        clock,
        startAt,
        summaries: payloads.map(summariseAppointmentPayload),
        day: dayLabel(payloads, competitionId, date),
    };
}
/** Why a transport's answer cannot be believed at all, or null. Reads the answer directly. */
function transportViolation(prepared, response) {
    const malformed = tmsHttpResponseProblem(response);
    if (malformed !== null)
        return malformed;
    if (prepared.redirects === "manual" && response.redirected) {
        return "it answered with a redirect it followed, although it promised never to follow one";
    }
    return null;
}
/** One guarded GET of the day page, read as far as it can be. Never throws for TMS's answers. */
async function loadDay(prepared, redactor, warnings) {
    guardTmsTechnicalRequest("GET", prepared.dayUrl, prepared.origin, prepared.originOptions);
    let response;
    try {
        response = await prepared.transport.getDayPage(prepared.competitionId, prepared.date, prepared.dayUrl);
    }
    catch (error) {
        return { kind: "lost", error: redactor.fragment(errorMessage(error)) };
    }
    const violation = transportViolation(prepared, response);
    if (violation !== null) {
        warnings.push(`The TMS Push transport broke its own rules reading the day page (${violation}). If it followed a redirect, the ` +
            "day's publish state may have changed: check it in TMS by eye, and report this.");
        return { kind: "violation", violation };
    }
    const answer = response;
    const read = readTechnicalDayResponse(answer, { competitionId: prepared.competitionId, date: prepared.date, base: prepared.origin });
    let messages = null;
    if (read.kind === "snapshot") {
        redactor.addToken(read.snapshot.token);
        messages = read.snapshot.messages;
    }
    else {
        warnings.push(...read.warnings);
        if (answer.status >= 200 && answer.status < 300) {
            const page = readTmsPage(answer.body);
            if (page.problems.length === 0)
                messages = messagesOf(page);
        }
    }
    return { kind: "page", status: answer.status, read, messages };
}
/** What the TD should do after a refusal made before anything was sent. */
function adviceFor(reason) {
    switch (reason) {
        case "session_expired":
            return "Sign in to TMS again, then press again.";
        case "unreachable":
            return "Check the connection to TMS, then press again.";
        case "form_changed":
            return "Enter this day's appointments in TMS by hand, and tell the build team that TMS's page has changed.";
        case "rejected_by_tms":
            return "Try again in a minute; if it persists, enter this day's appointments in TMS by hand.";
        case "transport_unavailable":
            return "Press again; if it persists, enter this day's appointments in TMS by hand and report it to the build team.";
    }
}
function loadRefusal(load, prepared, prefix = "") {
    const refused = (reason, detail, advice) => ({
        kind: "refused",
        reason,
        detail: `${prefix}${detail} Nothing was sent. ${advice ?? adviceFor(reason)}`,
        perMatch: new Map(),
        snapshot: null,
    });
    if (load.kind === "lost")
        return refused("unreachable", `TMS could not be reached to read the day page for ${prepared.day} (${load.error}).`, null);
    if (load.kind === "violation") {
        return refused("transport_unavailable", `The TMS Push transport broke its own rules reading the day page for ${prepared.day} (${load.violation}).`, null);
    }
    if (load.read.kind === "refused")
        return refused(load.read.reason, load.read.detail, load.read.advice);
    return null;
}
/** Read the day (twice if the first load shows error alerts) and plan. Sends nothing. */
async function readAndPlan(prepared, redactor, warnings) {
    const first = await loadDay(prepared, redactor, warnings);
    const firstRefusal = loadRefusal(first, prepared);
    if (firstRefusal !== null)
        return firstRefusal;
    let snapshot = first.read;
    if (snapshot.snapshot.messages.errors.length > 0 || snapshot.snapshot.messages.scriptErrors.length > 0) {
        // The page carries an error alert or error toast. If it is an earlier refused save's flash,
        // the selects show that save's OLD INPUT, not what TMS holds - so plan only from a later load.
        // Anything that stays on the second load is page furniture, and becomes the baseline.
        const again = await loadDay(prepared, redactor, warnings);
        const againRefusal = loadRefusal(again, prepared, "The day page was read a second time, because the first load showed an error alert that may have been an earlier save's answer. ");
        if (againRefusal !== null)
            return againRefusal;
        snapshot = again.read;
    }
    const plan = planTechnicalDayPost(snapshot.snapshot, prepared.payloads, { publishedMatches: prepared.policy });
    if (plan.kind === "refused") {
        return {
            kind: "refused",
            reason: plan.reason,
            detail: plan.detail,
            perMatch: new Map(plan.notPushed.map((item) => [item.tmsMatchDbId, item])),
            snapshot: snapshot.snapshot,
        };
    }
    return { kind: "planned", snapshot: snapshot.snapshot, plan };
}
function answerSentence(answer) {
    if (answer.kind === "lost")
        return `The connection failed before TMS's answer to the save arrived (${answer.error}).`;
    return answer.classification.detail;
}
function decideMatch(pushed, evidence, verdict) {
    const id = String(pushed.tmsMatchDbId);
    if (evidence.violations.length > 0) {
        return {
            outcome: "failed",
            reason: "unreachable",
            readBack: null,
            detail: `The save of match ${id} may or may not have landed in TMS: the TMS Push transport broke its own rules after sending it ` +
                `(${evidence.violations.join("; ")}), so nothing it read back is believed. Check match ${id} and the day's publish state ` +
                "in TMS by eye before pressing again, and report this to the build team.",
        };
    }
    const readBack = evidence.trusted === null ? null : tmsReadBackOf(evidence.trusted, pushed.tmsMatchDbId);
    const answer = evidence.answer;
    const classification = answer.kind === "answered" ? answer.classification : null;
    const already = TMS_APPOINTMENT_PUSH_SLOTS.every((slot) => pushed.before[slot] === pushed.sent[slot]);
    const shown = verdict === undefined
        ? ""
        : verdict.outcome === "accepted_draft"
            ? already
                ? ` The read-back shows ${crewText(pushed.sent)} for match ${id}, which TMS already held before this push.`
                : ` The read-back nevertheless shows ${crewText(pushed.sent)} for match ${id}: check it in TMS by eye.`
            : ` ${verdict.detail}`;
    if (classification !== null && classification.kind === "failed" && classification.definite) {
        return { outcome: "failed", reason: classification.reason, readBack, detail: `${classification.detail}${shown}` };
    }
    if (evidence.refusalWords.length > 0) {
        return {
            outcome: "failed",
            reason: "rejected_by_tms",
            readBack,
            detail: `TMS refused the save: ${quoted(evidence.refusalWords)}.${shown} Correct what TMS objected to, then press again.`,
        };
    }
    if (evidence.trusted === null || verdict === undefined) {
        return {
            outcome: "failed",
            reason: "unreachable",
            readBack: null,
            detail: `The save of match ${id} may or may not have landed in TMS. ${answerSentence(answer)} The day page could not be read ` +
                `back reliably afterwards: ${evidence.unverifiable} Check match ${id} in TMS by eye before pressing again; the other ` +
                "matches and publish boxes on this day could not be checked either.",
        };
    }
    if (verdict.outcome === "accepted_draft") {
        if (answer.kind === "lost") {
            return {
                outcome: "accepted_draft",
                readBack,
                detail: `The save's own answer was lost (${answer.error}), but the read-back shows the crew in TMS: ${verdict.detail}`,
            };
        }
        const words = answer.classification;
        let note;
        if (words.kind === "saved")
            note = words.confirmed ? ` ${words.detail}` : " TMS's own answer carried no confirmation; the read-back is the evidence.";
        else if (words.kind === "unknown") {
            note = evidence.toast === null ? ` ${words.detail}` : ` ${words.detail} The first page after the save confirmed: "${evidence.toast}".`;
        }
        else
            note = ` ${words.detail}`;
        return { outcome: "accepted_draft", readBack, detail: `${verdict.detail}${note}` };
    }
    if (answer.kind === "lost") {
        return {
            outcome: "failed",
            reason: "unreachable",
            readBack,
            detail: `TMS could not be reached to save match ${id} (${answer.error}). ${verdict.detail} Nothing is claimed to have landed. Check the connection to TMS, then press again.`,
        };
    }
    const reason = answer.classification.kind === "failed" ? answer.classification.reason : "rejected_by_tms";
    return {
        outcome: "failed",
        reason,
        readBack,
        detail: `${verdict.detail} ${answer.classification.detail} Check match ${id} in TMS, then press again.`,
    };
}
/** Collateral lines as the API takes them: redacted, cleaned, at most 300 characters, at most 100. */
function finalCollateral(redactor, lines) {
    const out = [];
    for (const line of lines) {
        const text = redactor.final(line, TMS_TECHNICAL_LIMITS.collateral);
        if (text !== "" && !out.includes(text))
            out.push(text);
    }
    if (out.length > TMS_TECHNICAL_LIMITS.collateralCount) {
        const hidden = out.length - (TMS_TECHNICAL_LIMITS.collateralCount - 1);
        out.length = TMS_TECHNICAL_LIMITS.collateralCount - 1;
        out.push(`...and ${String(hidden)} more warnings about this day. Check the whole day in TMS by eye.`);
    }
    return out;
}
/**
 * Push one competition-day of technical officials to TMS: read, plan, post ONCE, read twice.
 *
 * Resolves for every outcome once its arguments are sound - a refusal is a result, not an
 * exception - with one result per payload (each with its `readBack`), the day's collateral
 * warnings, the transport's name and the number of posts made (0 or 1). Arguments that describe a
 * call that should never have been made (a malformed payload, another competition's payload, a
 * repeated match, a bad date, a clock that gives no time, a base that is not TMS) throw before
 * any request. Nothing thrown after that escapes: a push that may have landed always resolves,
 * and says so.
 */
export async function pushTechnicalDay(transport, request, options = {}) {
    const prepared = prepareRequest(transport, request, options, "pushTechnicalDay");
    const { competitionId, date, payloads, summaries, day, name } = prepared;
    const redactor = new Redactor();
    const collateral = [];
    let posts = 0;
    /** Builds the result. Total: nothing in it can throw. */
    const finish = (decide) => {
        const at = safeInstant(prepared.clock, prepared.startAt);
        const results = payloads.map((payload, index) => {
            let decided;
            try {
                decided = decide(payload);
            }
            catch {
                decided =
                    posts === 0
                        ? { outcome: "failed", reason: "transport_unavailable", readBack: null, detail: "Nothing was sent to TMS: this build could not decide the result. Press again." }
                        : {
                            outcome: "failed",
                            reason: "unreachable",
                            readBack: null,
                            detail: `The save of match ${String(payload.tmsMatchDbId)} may or may not have landed in TMS. Check it in TMS by eye before pressing again.`,
                        };
            }
            const detail = redactor.final(decided.detail, TMS_TECHNICAL_LIMITS.detail) || "No detail was recorded. Check this match in TMS by eye.";
            const shared = {
                tmsMatchDbId: payload.tmsMatchDbId,
                readBack: decided.readBack,
                summary: redactor.final(summaries[index] ?? "", 400),
                transport: name,
            };
            return decided.outcome === "accepted_draft"
                ? { outcome: "accepted_draft", at, tmsReference: null, detail, ...shared }
                : { outcome: "failed", at, reason: decided.reason, detail, ...shared };
        });
        return { transport: name, competitionId, date, posts, results, collateral: finalCollateral(redactor, collateral) };
    };
    const everyPayload = (reason, detail) => finish(() => ({ outcome: "failed", reason, detail, readBack: null }));
    try {
        /* Step 1: read the day (twice if it shows an error alert) and plan. */
        const opened = await readAndPlan(prepared, redactor, collateral);
        if (opened.kind === "refused") {
            return finish((payload) => {
                const own = opened.perMatch.get(payload.tmsMatchDbId);
                return own === undefined
                    ? { outcome: "failed", reason: opened.reason, detail: opened.detail, readBack: null }
                    : { outcome: "failed", reason: own.reason, detail: own.detail, readBack: null };
            });
        }
        const { snapshot, plan } = opened;
        redactor.addSecret(plan.body);
        /* Step 2: the caller's last word - the extension's hold check. */
        let stop = null;
        if (options.beforePost !== undefined) {
            try {
                const answer = options.beforePost();
                const ok = answer !== null && typeof answer === "object" && answer.ok === true;
                if (!ok) {
                    const words = answer !== null && typeof answer === "object" ? answer.detail : undefined;
                    stop = typeof words === "string" && words.trim() !== "" ? redactor.final(words, 600) : "the push was stopped just before it was sent.";
                }
            }
            catch (error) {
                stop = `the push was stopped just before it was sent (${redactor.fragment(errorMessage(error))}).`;
            }
        }
        if (stop !== null)
            return everyPayload("transport_unavailable", `Nothing was sent to TMS: ${stop}`);
        /* Step 3: the post, exactly once. A failed post is answered, never repeated. */
        guardTmsTechnicalRequest("POST", prepared.postUrl, prepared.origin, prepared.originOptions);
        posts = 1;
        const violations = [];
        let postAnswer;
        try {
            const response = await transport.postTechnical(competitionId, plan.body, prepared.postUrl);
            const violation = transportViolation(prepared, response);
            if (violation !== null) {
                violations.push(violation);
                collateral.push(`The TMS Push transport broke its own rules answering the save (${violation}). If it followed a redirect, the ` +
                    "day's publish state may have changed: check it in TMS by eye, and report this.");
                postAnswer = { kind: "answered", classification: { kind: "unknown", detail: `TMS's answer to the save cannot be believed (${violation}).` } };
            }
            else {
                const readable = response;
                postAnswer = { kind: "answered", classification: classifyTechnicalPostResponse(readable, snapshot.messages) };
                const endedAt = describeTmsResponseUrl(readable.url);
                if (endedAt.kind === "publish") {
                    collateral.push(`TMS's answer to the save ended at a publish or unpublish address (${fragment(endedAt.path)}), so the day's ` +
                        "publish state may have changed. Check it in TMS by eye.");
                }
            }
        }
        catch (error) {
            // No answer - or one that could not even be read. The post may still have landed: read back.
            postAnswer = { kind: "lost", error: redactor.fragment(errorMessage(error)) };
        }
        /* Step 4: read the day for TMS's words (this load takes any flash), then again for the slots. */
        const first = await loadDay(prepared, redactor, collateral);
        const second = await loadDay(prepared, redactor, collateral);
        for (const load of [first, second])
            if (load.kind === "violation")
                violations.push(load.violation);
        const baseline = snapshot.messages;
        const toast = (postAnswer.kind === "answered" && postAnswer.classification.kind === "saved" && postAnswer.classification.confirmed
            ? postAnswer.classification.detail
            : null) ??
            (first.kind === "page" ? first.messages?.saved ?? null : null);
        const refusalWords = [];
        for (const load of [first, second]) {
            if (load.kind !== "page" || load.messages === null)
                continue;
            refusalWords.push(...newAlerts(load.messages, baseline));
            if (toast === null && load.messages.guardText !== null && load.messages.guardText !== baseline.guardText)
                refusalWords.push(load.messages.guardText);
        }
        let trusted = null;
        let unverifiable = "";
        if (first.kind === "lost") {
            unverifiable = `the first read after the save failed (${first.error}), so the next page could have shown what the save asked for instead of what TMS holds.`;
        }
        else if (second.kind === "lost") {
            unverifiable = `TMS could not be reached (${second.error}).`;
        }
        else if (second.kind === "violation") {
            unverifiable = `the TMS Push transport broke its own rules (${second.violation}).`;
        }
        else if (second.read.kind === "refused") {
            unverifiable = second.read.detail;
        }
        else if (second.messages !== null && newAlerts(second.messages, baseline).length > 0) {
            unverifiable = "the page read back still showed an error from TMS, so its selections may show what a save asked for rather than what TMS holds.";
        }
        else {
            trusted = second.read.snapshot;
        }
        const evidence = { answer: postAnswer, violations, refusalWords: [...new Set(refusalWords)], trusted, unverifiable, toast };
        const verification = trusted === null || violations.length > 0 ? null : verifyTechnicalDay(plan.expected, trusted);
        if (verification !== null)
            collateral.push(...verification.collateral);
        else if (violations.length === 0) {
            collateral.push("The day could not be read back reliably after the save, so no crew or publish box on it could be checked. Check " +
                "the whole day in TMS by eye.");
        }
        const verdicts = new Map((verification?.matches ?? []).map((verdict) => [verdict.tmsMatchDbId, verdict]));
        const pushedById = new Map(plan.expected.pushed.map((pushed) => [pushed.tmsMatchDbId, pushed]));
        const notPushed = new Map(plan.notPushed.map((item) => [item.tmsMatchDbId, item]));
        /* Step 5: decide, match by match. */
        return finish((payload) => {
            const own = notPushed.get(payload.tmsMatchDbId);
            if (own !== undefined) {
                const readBack = trusted === null || violations.length > 0 ? null : tmsReadBackOf(trusted, payload.tmsMatchDbId);
                return { outcome: "failed", reason: own.reason, detail: own.detail, readBack };
            }
            const pushed = pushedById.get(payload.tmsMatchDbId);
            if (pushed === undefined) {
                return {
                    outcome: "failed",
                    reason: "unreachable",
                    readBack: null,
                    detail: `Match ${String(payload.tmsMatchDbId)} was not accounted for by this push. Check it in TMS by eye before pressing again.`,
                };
            }
            return decideMatch(pushed, evidence, verdicts.get(payload.tmsMatchDbId));
        });
    }
    catch (error) {
        // Nothing escapes once the call is sound. Before the post, nothing was sent; after it, the
        // save may have landed - a caller that saw an exception would take it for "nothing happened".
        const why = redactor.fragment(errorMessage(error));
        if (posts === 0) {
            return everyPayload("transport_unavailable", `Nothing was sent to TMS: the push failed before the save (${why}). Press again; if it persists, enter this day's ` +
                "appointments in TMS by hand and report it to the build team.");
        }
        return everyPayload("unreachable", `The save of ${day} may or may not have landed in TMS: the push failed after sending it (${why}). Check the whole day in ` +
            "TMS by eye before pressing again; no crew or publish box on it could be checked.");
    }
}
/** The person TMS holds in a slot, with the label of the option TMS marks selected. */
function heldPerson(state) {
    return { personId: state.current ?? "0", label: state.currentLabel };
}
function buildPreview(prepared, opened, redactor, warnings) {
    const at = safeInstant(prepared.clock, prepared.startAt);
    const clean = (text, limit) => redactor.final(text, limit);
    const snapshot = opened.snapshot;
    const payloadById = new Map(prepared.payloads.map((payload, index) => [payload.tmsMatchDbId, { payload, summary: prepared.summaries[index] ?? "" }]));
    const matches = [];
    for (const match of snapshot?.matches ?? []) {
        const planned = payloadById.get(match.tmsMatchDbId);
        const role = !match.editable
            ? "official"
            : planned === undefined
                ? "unplanned"
                : match.published === true && prepared.policy === "refuse"
                    ? "published"
                    : "push";
        const current = {};
        const after = {};
        const notes = [];
        const changes = [];
        for (const slot of TMS_APPOINTMENT_PUSH_SLOTS) {
            const state = match.slots[slot];
            current[slot] = heldPerson(state);
            if (role === "push" && planned !== undefined) {
                const official = planned.payload.slots[slot];
                const check = checkPlannedSlot(state, slot, official, matchLabel(planned.payload));
                after[slot] = { personId: official.tmsPersonId, label: check.label };
                if (check.problem !== null)
                    notes.push(clean(check.problem, TMS_TECHNICAL_LIMITS.collateral));
                if (official.tmsPersonId !== (state.current ?? "0"))
                    changes.push(slot);
            }
            else {
                after[slot] = current[slot];
            }
        }
        if (role === "official") {
            notes.push(planned === undefined
                ? `Official in TMS: not sent, and left exactly as it is ("${TMS_OFFICIAL_MATCH_RULE}").`
                : `Not sent: Official in TMS ("${TMS_OFFICIAL_MATCH_RULE}"). Remove it from the push, or change it in TMS by hand.`);
        }
        else if (role === "published") {
            notes.push(`Not sent: ${TMS_PUBLISHED_MATCH_RULE}`);
        }
        else if (role === "unplanned") {
            notes.push("Not in this push: sent back exactly as TMS holds it.");
        }
        else if (match.published === true) {
            notes.push("Its publish box is ticked in TMS, so the new crew will be public as soon as it is saved.");
        }
        matches.push({
            tmsMatchDbId: match.tmsMatchDbId,
            role,
            planned: planned !== undefined,
            official: match.official,
            published: match.published,
            status: clean(match.status, 60),
            tmsRow: {
                kickOff: match.tmsRow.kickOff === null ? null : clean(match.tmsRow.kickOff, 40) || null,
                matchText: match.tmsRow.matchText === null ? null : clean(match.tmsRow.matchText, TMS_TECHNICAL_LIMITS.fragment) || null,
            },
            current: Object.fromEntries(TMS_APPOINTMENT_PUSH_SLOTS.map((slot) => [slot, cleanPerson(current[slot], clean)])),
            after: Object.fromEntries(TMS_APPOINTMENT_PUSH_SLOTS.map((slot) => [slot, cleanPerson(after[slot], clean)])),
            changes,
            summary: planned === undefined ? null : clean(planned.summary, 400),
            notes,
        });
    }
    const onDay = new Set((snapshot?.matches ?? []).map((match) => match.tmsMatchDbId));
    const notOnTmsDay = snapshot === null
        ? []
        : prepared.payloads
            .filter((payload) => !onDay.has(payload.tmsMatchDbId))
            .map((payload) => ({ tmsMatchDbId: payload.tmsMatchDbId, summary: clean(payloadById.get(payload.tmsMatchDbId)?.summary ?? "", 400) }));
    const refusal = opened.kind === "refused" ? { reason: opened.reason, detail: clean(opened.detail, TMS_TECHNICAL_LIMITS.detail) } : null;
    return {
        transport: prepared.name,
        competitionId: prepared.competitionId,
        date: prepared.date,
        at,
        posts: 0,
        ready: refusal === null && matches.some((match) => match.role === "push"),
        refusal,
        matches,
        notOnTmsDay,
        warnings: finalCollateral(redactor, warnings),
    };
}
function cleanPerson(person, clean) {
    return { personId: person.personId, label: person.label === null ? null : clean(person.label, TMS_TECHNICAL_LIMITS.fragment) };
}
/**
 * READ-ONLY. What a push would do, for the extension's confirm window: one guarded GET of the day
 * page (a second only if the first shows an error alert - it may be an earlier save's flash),
 * parse, plan - and NO post. For every match on TMS's day: what TMS holds now, with TMS's own
 * labels; what each planned slot will become; which matches are Official (not pushed), published,
 * or unplanned (sent back as TMS holds them); and any refusal. Programmer errors throw before any
 * request, exactly as {@link pushTechnicalDay}'s do; nothing after that escapes.
 */
export async function previewTechnicalDay(transport, request, options = {}) {
    const prepared = prepareRequest(transport, request, options, "previewTechnicalDay");
    const redactor = new Redactor();
    const warnings = [];
    let opened;
    try {
        opened = await readAndPlan(prepared, redactor, warnings);
    }
    catch (error) {
        opened = {
            kind: "refused",
            reason: "transport_unavailable",
            detail: `The preview failed while reading TMS's day page for ${prepared.day} (${redactor.fragment(errorMessage(error))}). Nothing was sent. Press again.`,
            perMatch: new Map(),
            snapshot: null,
        };
    }
    try {
        return buildPreview(prepared, opened, redactor, warnings);
    }
    catch {
        return {
            transport: prepared.name,
            competitionId: prepared.competitionId,
            date: prepared.date,
            at: prepared.startAt,
            posts: 0,
            ready: false,
            refusal: { reason: "transport_unavailable", detail: "The preview could not be built. Nothing was sent. Press again." },
            matches: [],
            notOnTmsDay: [],
            warnings: [],
        };
    }
}
/**
 * The results stripped to the API's per-result shape (no `tmsReference`, `summary` or
 * `transport`, which the API refuses as unknown fields). Collateral is already within the API's
 * bounds on the push result itself.
 */
export function toTmsPushReportResults(result) {
    return result.results.map((item) => item.outcome === "accepted_draft"
        ? { tmsMatchDbId: item.tmsMatchDbId, outcome: "accepted_draft", detail: item.detail, at: item.at, readBack: item.readBack }
        : { tmsMatchDbId: item.tmsMatchDbId, outcome: "failed", reason: item.reason, detail: item.detail, at: item.at, readBack: item.readBack });
}
//# sourceMappingURL=tms-technical-day.js.map